import type React from "react";
import { useTranslation } from "react-i18next";
import { BasePostInput } from "@/components/common/BasePostInput";
import type { EditableFile } from "@/components/common/ExistingFileAttachment";
import { ExistingFileAttachment } from "@/components/common/ExistingFileAttachment";
import { FileAttachment } from "@/components/common/FileAttachment";
import { useMentionInputNotice } from "@/hooks/useMentionInputNotice";

interface PostCardEditFormProps {
  value: string;
  originalContent: string;
  isSubmitting: boolean;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onCancel: () => void;
  onSubmit: (content?: string) => Promise<void>;
  existingFiles?: EditableFile[];
  filesToRemove?: Set<number>;
  filesToReplace?: Map<number, File>;
  filesToAdd?: File[];
  addFilesInputRef?: React.RefObject<HTMLInputElement | null>;
  onRemoveExistingFile?: (fileId: number) => void;
  onReplaceExistingFile?: (fileId: number, newFile: File) => void;
  onRemoveNewFile?: (index: number) => void;
  onAddFilesInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const PostCardEditForm: React.FC<PostCardEditFormProps> = ({
  value,
  originalContent,
  isSubmitting,
  onChange,
  onCancel,
  onSubmit,
  existingFiles = [],
  filesToRemove = new Set(),
  filesToReplace = new Map(),
  filesToAdd = [],
  addFilesInputRef,
  onRemoveExistingFile,
  onReplaceExistingFile,
  onRemoveNewFile,
  onAddFilesInputChange,
}) => {
  const { t } = useTranslation();
  const hasTypedMentionInEdit = useMentionInputNotice(
    value,
    originalContent,
    originalContent,
  );

  const hasFileChanges =
    filesToRemove.size > 0 || filesToReplace.size > 0 || filesToAdd.length > 0;

  return (
    <div className="space-y-2">
      {existingFiles.length > 0 &&
        onRemoveExistingFile &&
        onReplaceExistingFile && (
          <ExistingFileAttachment
            files={existingFiles}
            filesToRemove={filesToRemove}
            filesToReplace={filesToReplace}
            onRemove={onRemoveExistingFile}
            onReplace={onReplaceExistingFile}
          />
        )}
      <BasePostInput
        initialContent={originalContent}
        initialDraftKey={`post-edit:${originalContent}`}
        onContentChange={onChange}
        onSubmit={async (content) => onSubmit(content)}
        onCancel={onCancel}
        submitButtonLabel={t("common.save")}
        cancelButtonLabel={t("common.cancel")}
        disabled={isSubmitting}
        alwaysVisible
        showFormatting
        showAttachButton={Boolean(addFilesInputRef && onAddFilesInputChange)}
        externalOnAttachClick={() => addFilesInputRef?.current?.click()}
        externalFileInputRef={addFilesInputRef}
        externalOnFileChange={onAddFilesInputChange}
        hasDiscardableChanges={
          value.trim() !== originalContent.trim() || hasFileChanges
        }
        containerClassName="!rounded-md !border-0 !bg-transparent !p-0 !shadow-none"
        className="max-h-[52vh] min-h-40 overflow-y-auto rounded-2xl [&_[data-editor-footer]]:sticky [&_[data-editor-footer]]:bottom-0 [&_[data-editor-footer]]:z-20 [&_[data-editor-footer]]:bg-white"
      />
      {hasTypedMentionInEdit && (
        <div className="rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-700">
          {t("common.editMentionNoNotificationWarning")}
        </div>
      )}
      {filesToAdd.length > 0 && onRemoveNewFile && (
        <FileAttachment
          files={filesToAdd}
          fileError={null}
          onRemoveFile={onRemoveNewFile}
        />
      )}
    </div>
  );
};
